import { useEffect, useRef } from 'react';
import { DataService } from '../services/DataService';

const SYNC_INTERVAL_MS = 90 * 1000;
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart'];

// Background sync that skips ticks when the tab is hidden or the user has been
// idle for more than IDLE_THRESHOLD_MS. Returning to activity (any input, or
// the tab regaining visibility) fires an immediate catch-up sync so users
// don't have to wait up to SYNC_INTERVAL_MS for fresh data.
export function useIdleSync(token) {
  const lastActivityRef = useRef(Date.now());
  const wasIdleRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    const trySync = () => {
      if (navigator.onLine) DataService.sync();
    };

    // Only sync on the idle→active transition, not every click — otherwise a
    // user clicking through the UI would fire a sync per interaction.
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (wasIdleRef.current) {
        wasIdleRef.current = false;
        trySync();
      }
    };

    // Foregrounding is the strongest wake signal — Capacitor's WKWebView also
    // fires this when the iOS app returns from background.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      lastActivityRef.current = Date.now();
      wasIdleRef.current = false;
      trySync();
    };

    const interval = setInterval(() => {
      const hidden = document.visibilityState === 'hidden';
      const idle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS;
      if (hidden || idle) {
        wasIdleRef.current = true;
        return;
      }
      trySync();
    }, SYNC_INTERVAL_MS);

    // capture: true so we still see events that downstream handlers stopPropagation on.
    const opts = { capture: true, passive: true };
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, opts));
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity, opts));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [token]);
}
